// ─────────────────────────────────────────────────────────────────────────────
// Admin Types
// ─────────────────────────────────────────────────────────────────────────────

export type AIBehavior     = 'aggressive' | 'defensive' | 'support';
export type CellTypeAdmin  = 'Walkable' | 'Full_Obstacle' | 'Vision_Obstacle' | 'Tree' | 'Mountain' | 'Rock' | 'Lake' | 'High_Ground' | 'Ramp';

export interface ResistanceAdmin {
  element:    'Fire' | 'Earth' | 'Air' | 'Water' | 'Neutral';
  percentage: number;
  flat:       number;
}

/**
 * Template de monstro — espelha MonsterTemplate.php::toArray().
 * Todos os campos opcionais (id, created_at, updated_at) são gerenciados pelo backend.
 */
export interface MonsterTemplate {
  id?:                    string;
  name:                   string;
  description:            string;
  icon_url:               string;
  level:                  number;
  /** XP concedido ao jogador ao derrotar este monstro. */
  xp_reward:              number;
  /**
   * Pontos extras de ranking concedidos ao jogador que abater este monstro.
   * É SOMADO à regra global 'monsterKill' configurada no painel de Ranking.
   * Use 0 para monstros comuns e valores altos para chefes/bosses.
   */
  ranking_points:         number;
  hp_max:                 number;
  ap_max:                 number;
  mp_max:                 number;
  strength:               number;
  intelligence:           number;
  agility:                number;
  luck:                   number;
  wisdom:                 number;
  initiative:             number;
  dodge:                  number;
  block:                  number;
  elemental_resistances:  ResistanceAdmin[];
  spell_ids:              string[];
  ai_behavior:            AIBehavior;
  /** Nome do sprite sheet sem extensão — corresponde a /public/sprites/<sprite>.png */
  sprite:                 string;
  /** Se o monstro está ativo e pode spawnar no jogo */
  active?:                boolean;
  /** Se o nível do monstro e seus status vão escalar acompanhando o do jogador (+/- 20%) */
  is_dynamic_level?:      boolean;
  created_at?:            string;
  updated_at?:            string;
}

/**
 * Valores padrão para o formulário de criação de monstro.
 * Espelha os defaults do construtor MonsterTemplate.php.
 */
export const MONSTER_DEFAULTS: Omit<MonsterTemplate, 'id' | 'created_at' | 'updated_at'> = {
  name:            '',
  description:     '',
  icon_url:        '',
  level:           1,
  xp_reward:       50,
  // 0 = sem bônus individual; usa apenas a regra global de ranking
  ranking_points:  0,
  hp_max:          100,
  ap_max:          6,
  mp_max:          3,
  strength:        0,
  intelligence:    0,
  agility:         0,
  luck:            0,
  wisdom:          0,
  initiative:      10,
  dodge:           0,
  block:           0,
  spell_ids:       [],
  ai_behavior:     'aggressive',
  sprite:          '',
  active:          true,
  is_dynamic_level: false,
  elemental_resistances: [
    { element: 'Fire',    percentage: 0, flat: 0 },
    { element: 'Earth',   percentage: 0, flat: 0 },
    { element: 'Air',     percentage: 0, flat: 0 },
    { element: 'Water',   percentage: 0, flat: 0 },
    { element: 'Neutral', percentage: 0, flat: 0 },
  ],
};

/** Estilo visual/mecânico da Via de Subida Ponto A → Ponto B entre duas células vizinhas. */
export type ClimbConnectionType = 'RAMP' | 'STAIRS' | 'ROPE';

/**
 * Via de Subida (Ponto A → Ponto B) — conecta esta célula a uma vizinha
 * ortogonal de nível diferente, liberando passagem do personagem entre os
 * dois níveis e trocando o muro vertical por um elemento de transição
 * (rampa/escada/corda). Gravada simetricamente nas duas pontas pela
 * ferramenta "Subida" do Editor de Mapas.
 */
export interface ClimbConnection {
  targetX: number;
  targetY: number;
  type:    ClimbConnectionType;
}

export interface AdminCell {
  x:    number;
  y:    number;
  type: CellTypeAdmin;
  /**
   * Nível de Elevação de Terreno (0-2) — pintado com a ferramenta
   * "Nível de Terreno / Elevação" no Editor de Mapas do Admin.
   * 0 = Chão Padrão, 1 = Rampa/Planalto, 2 = Cume da Montanha/Plataforma Mais Alta.
   */
  elevation?: number;
  /** Via de Subida explícita ligando esta célula a uma vizinha (ver `ClimbConnection`). */
  climb_connection?: ClimbConnection | null;
  /** Revestimento do piso caminhável ('dirt' = chão batido). Ausente = grama. */
  floor?: 'grass' | 'dirt';
}

/** Tema do Plano de Fundo Medieval Dinâmico (Parallax Ambiance) selecionável no Admin. */
export type MapBackgroundTheme = 'medieval_mountains_day' | 'dark_castle_night' | 'enchanted_forest';

export const BACKGROUND_THEME_OPTIONS: { value: MapBackgroundTheme; labelKey: string }[] = [
  { value: 'medieval_mountains_day', labelKey: 'bgTheme.medieval_mountains_day' },
  { value: 'dark_castle_night',      labelKey: 'bgTheme.dark_castle_night' },
  { value: 'enchanted_forest',       labelKey: 'bgTheme.enchanted_forest' },
];

/**
 * Tema de Clima/Horário (Sistema de Iluminação e Clima Variável) selecionável
 * no Admin — independente do tema de cenário de fundo acima. Controla o
 * overlay de <CanvasWeatherEffect/> no frontend do jogador.
 */
export type MapWeatherType = 'SUNNY' | 'SUNSET' | 'NIGHT' | 'CLOUDY' | 'RAIN' | 'FOG' | 'STORM';

/**
 * Valor de CONFIGURAÇÃO do mapa (admin) — inclui 'AUTO' além dos climas
 * concretos de `MapWeatherType`. 'AUTO' nunca chega a um Combat real: o
 * backend sorteia um clima concreto ao iniciar cada batalha (ver
 * `TurnMachineService::resolveWeatherType`), então o jogador em combate
 * sempre vê um clima de verdade, nunca "Automático" na tela.
 */
export type MapWeatherConfig = MapWeatherType | 'AUTO';

export const WEATHER_TYPE_OPTIONS: { value: MapWeatherConfig; labelKey: string }[] = [
  { value: 'AUTO',   labelKey: 'weatherType.AUTO' },
  { value: 'SUNNY',  labelKey: 'weatherType.SUNNY' },
  { value: 'SUNSET', labelKey: 'weatherType.SUNSET' },
  { value: 'NIGHT',  labelKey: 'weatherType.NIGHT' },
  { value: 'CLOUDY', labelKey: 'weatherType.CLOUDY' },
  { value: 'RAIN',   labelKey: 'weatherType.RAIN' },
  { value: 'FOG',    labelKey: 'weatherType.FOG' },
  { value: 'STORM',  labelKey: 'weatherType.STORM' },
];

/**
 * Limite de rotação da câmera (arrasto do botão direito) no combate, em graus
 * de arco TOTAL — 360 = livre (sem trava), 180/90 = arco reduzido, 120 =
 * padrão (mesmo comportamento de ±60° de antes desta configuração existir).
 */
export type CameraRotationDeg = 90 | 120 | 180 | 360;

export const CAMERA_ROTATION_OPTIONS: { value: CameraRotationDeg; labelKey: string }[] = [
  { value: 120, labelKey: 'cameraRotation.default' },
  { value: 90,  labelKey: 'cameraRotation.deg90' },
  { value: 180, labelKey: 'cameraRotation.deg180' },
  { value: 360, labelKey: 'cameraRotation.deg360' },
];

export interface MapTemplate {
  id?:                 string;
  name:                string;
  description:         string;
  width:               number;
  height:              number;
  cells:               AdminCell[][];
  /** Tema do Plano de Fundo Medieval Dinâmico. Default: 'medieval_mountains_day'. */
  background_theme?:   MapBackgroundTheme;
  /** Tema de Clima/Horário, ou 'AUTO' pro sistema sortear a cada batalha. Default: 'SUNNY'. */
  weather_type?:       MapWeatherConfig;
  /** Toggle "Exibir Fundo (Céu)" — liga/desliga o cenário parallax animado. Default: true. */
  background_enabled?: boolean;
  /** Limite de rotação da câmera de combate, em graus de arco total. Default: 120 (±60°). */
  camera_rotation?:    CameraRotationDeg;
  /** Lado do mapa (visão de cima do editor) que é o Norte da bússola. Default: 0 (topo). */
  north?:              NorthSide;
  created_at?:         string;
  updated_at?:         string;
}

/** Lado do mapa que é o Norte: 0=topo, 90=direita, 180=baixo, 270=esquerda. */
export type NorthSide = 0 | 90 | 180 | 270;
export const NORTH_SIDES: NorthSide[] = [0, 90, 180, 270];

export const MAP_DEFAULTS: Omit<MapTemplate, 'id' | 'created_at' | 'updated_at'> = {
  name:        '',
  description: '',
  width:       14,
  height:      20,
  cells:       [],
  background_theme: 'medieval_mountains_day',
  weather_type:     'SUNNY',
  background_enabled: true,
  camera_rotation: 120,
  north: 0,
};

// ── Launch Combat ─────────────────────────────────────────────────────────────

export interface EnemyEntry {
  template_id: string;
  count:       number;
}

export interface LaunchCombatPayload {
  map_id:            string;
  enemies:           EnemyEntry[];
  include_player?:   boolean;
  player_name?:      string;
  player_class?:     string;
  player_spell_ids?: string[];
}

export interface LaunchCombatResult {
  combat_id:         string;
  player_entity_id:  string;
  url:               string;
}

// ── User Management ───────────────────────────────────────────────────────────

export interface AdminCharacterStats {
  hp:           number;
  ap:           number;
  mp:           number;
  strength:     number;
  intelligence: number;
  agility:      number;
  initiative:   number;
}

export interface AdminCharacter {
  id:        string;
  name:      string;
  class:     string;
  createdAt: string;
  stats:     AdminCharacterStats | null;
}

export interface AdminUser {
  id:        string;
  email:     string;
  role:      'user' | 'admin';
  blocked:   boolean;
  online:    boolean;
  character: AdminCharacter | null;
  createdAt: string;
}

export interface SpellData {
  id:              string;
  name:            string;
  description?:    string;
  icon_url?:       string;
  shortcut?:       string;
  ap_cost:         number;
  range_min:       number;
  range_max:       number;
  line_of_sight:   boolean;
  casts_per_turn:  number;
  cooldown:        number;
  effect_area:     { type: string; size: number };
  effects:         Array<{ type: string; element?: string; min_value: number; max_value: number }>;
}

// ── Character Classes ─────────────────────────────────────────────────────────

export type CharacterClassSlug = 'warrior' | 'mage' | 'rogue' | 'archer' | 'cleric' | 'ferreiro';
export type PrimaryElement     = 'Fire' | 'Earth' | 'Air' | 'Water' | 'Neutral';
export type PrimaryAttribute   = 'strength' | 'intelligence' | 'agility' | 'luck' | 'wisdom';

export interface CharacterClassData {
  id?:                string;
  slug:               CharacterClassSlug;
  display_name:       string;
  description:        string;
  primary_element:    PrimaryElement;
  primary_attribute:  PrimaryAttribute;
  sprite:             string;
  icon_url:           string;
  hp_max:             number;
  ap_max:             number;
  mp_max:             number;
  strength:           number;
  intelligence:       number;
  agility:            number;
  luck:               number;
  wisdom:             number;
  initiative:         number;
  dodge:              number;
  block:              number;
  spell_ids:          string[];
  inventory_capacity?: number;
  created_at?:        string;
  updated_at?:        string;
}
